import type { PassphraseVerifier } from '@life-os/app-core';
import { PBKDF2_ITERATIONS } from '@life-os/app-core';

/**
 * Local passphrase hashing for the app lock. Uses Web Crypto PBKDF2-SHA256,
 * available in both the browser and Node 20+. The passphrase is never stored;
 * only salt + iterations + derived hash are persisted.
 */

const enc = new TextEncoder();

function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriveHashB64(passphrase: string, salt: Uint8Array, iterations: number): Promise<string> {
  const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    keyMaterial,
    256,
  );
  return bytesToB64(new Uint8Array(bits));
}

/** Build a fresh verifier from a passphrase (random 16-byte salt). */
export async function createVerifier(passphrase: string): Promise<PassphraseVerifier> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iterations = PBKDF2_ITERATIONS;
  const hashB64 = await deriveHashB64(passphrase, salt, iterations);
  return { saltB64: bytesToB64(salt), iterations, hashB64 };
}

/** Constant-time-ish comparison of two equal-length base64 strings. */
function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Verify a passphrase against a stored verifier. */
export async function verifyPassphrase(passphrase: string, verifier: PassphraseVerifier): Promise<boolean> {
  const candidate = await deriveHashB64(passphrase, b64ToBytes(verifier.saltB64), verifier.iterations);
  return safeEqual(candidate, verifier.hashB64);
}
